/**
 * ChatStatsFooter — expandable performance stats below assistant messages.
 *
 * Collapsed: "generated in 1:23.45"
 * Expanded: pipeline steps, inference details, hardware context, copy button.
 */

import { useState, useCallback } from 'react';
import type { QueryStats } from '@skjv/common';
import { ClockIcon, CopyIcon, CheckIcon, ChevronUpIcon } from '@components/icons';
import { formatDuration } from './format-duration';
import { getExpectedRange, getVariance } from './expected-ranges';

interface ChatStatsFooterProps {
  stats: QueryStats;
  intent?: string;
}

export function ChatStatsFooter({ stats, intent }: ChatStatsFooterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildCopyText(stats, intent));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    } catch { /* clipboard may not be available */ }
  }, [stats, intent]);

  return (
    <div className="chat-stats-footer">
      <button
        className="chat-stats-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ClockIcon className="h-3 w-3" />
        <span>generated in {formatDuration(stats.totalDurationMs)}</span>
        {stats.context?.model && (
          <span className="chat-stats-model" title="AI model">{stats.context.model}</span>
        )}
        <ChevronUpIcon className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? '' : 'rotate-180'}`} />
      </button>

      {isExpanded && (
        <div className="chat-stats-details">
          <StatsSection title="Pipeline">
            {stats.steps.map((step) => (
              <StatsRow key={step.name} label={step.name} value={formatDuration(step.durationMs)} />
            ))}
            <div className="chat-stats-divider" />
            <StatsRow label="total" value={formatDuration(stats.totalDurationMs)} />
          </StatsSection>

          {stats.inference && (
            <StatsSection title="Inference">
              <StatsRow label="model" value={stats.context.model} />
              <StatsRow label="prompt tokens" value={stats.inference.promptTokens.toLocaleString()} />
              <StatsRow label="completion tokens" value={stats.inference.completionTokens.toLocaleString()} />
              <StatsRow label="tokens/sec" value={stats.inference.tokensPerSecond.toLocaleString()} />
            </StatsSection>
          )}

          <StatsSection title="Hardware">
            <StatsRow label="host" value={stats.context.hostname} />
            {stats.context.cpuModel && (
              <StatsRow label="cpu" value={stats.context.cpuModel} />
            )}
            {stats.context.cpuCores && (
              <StatsRow label="cores" value={String(stats.context.cpuCores)} />
            )}
            {stats.context.totalMemoryGb && (
              <StatsRow label="ram" value={`${stats.context.totalMemoryGb} GB total / ${stats.context.freeMemoryGb ?? '?'} GB free`} />
            )}
            {stats.context.platform && (
              <StatsRow label="platform" value={`${stats.context.platform} ${stats.context.arch ?? ''}`} />
            )}
            <StatsRow label="engine" value={stats.context.ollamaHost.replace('http://', '')} />
            <ExpectedRangeRow durationMs={stats.totalDurationMs} intent={intent} />
          </StatsSection>

          <button className="chat-stats-copy" onClick={handleCopy}>
            {copySuccess
              ? <><CheckIcon className="h-3 w-3 text-green-500" /><span>Copied!</span></>
              : <><CopyIcon className="h-3 w-3" /><span>Copy</span></>
            }
          </button>
        </div>
      )}
    </div>
  );
}

function StatsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chat-stats-section">
      <div className="chat-stats-section-title">{title}</div>
      {children}
    </div>
  );
}

function StatsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="chat-stats-row">
      <span className="chat-stats-label">{label}</span>
      <span className="chat-stats-value">{value}</span>
    </div>
  );
}

function ExpectedRangeRow({ durationMs, intent }: { durationMs: number; intent?: string }) {
  const [low, high] = getExpectedRange(intent);
  const variance = getVariance(durationMs, intent);
  const colors = { fast: 'text-green-500', normal: 'text-stone-400', slow: 'text-amber-500' };
  return (
    <>
      <StatsRow label="expected range" value={`${formatDuration(low)}-${formatDuration(high)}`} />
      <div className="chat-stats-row">
        <span className="chat-stats-label">variance</span>
        <span className={`chat-stats-value ${colors[variance]}`}>{variance}</span>
      </div>
    </>
  );
}

function buildCopyText(stats: QueryStats, intent?: string): string {
  const lines = [
    'SKJV Chat Stats',
    `Generated: ${stats.context.timestamp}`,
    `Total: ${formatDuration(stats.totalDurationMs)}`,
    '',
    'Pipeline:',
    ...stats.steps.map((s) => `  ${s.name.padEnd(22)} ${formatDuration(s.durationMs)}`),
  ];
  if (stats.inference) {
    lines.push('', 'Inference:',
      `  Model: ${stats.context.model}`,
      `  Prompt tokens: ${stats.inference.promptTokens.toLocaleString()}`,
      `  Completion tokens: ${stats.inference.completionTokens.toLocaleString()}`,
      `  Tokens/sec: ${stats.inference.tokensPerSecond.toLocaleString()}`,
    );
  }
  const [low, high] = getExpectedRange(intent);
  const variance = getVariance(stats.totalDurationMs, intent);
  lines.push('', 'Hardware:',
    `  Host: ${stats.context.hostname}`,
  );
  if (stats.context.cpuModel) lines.push(`  CPU: ${stats.context.cpuModel}`);
  if (stats.context.cpuCores) lines.push(`  Cores: ${stats.context.cpuCores}`);
  if (stats.context.totalMemoryGb) {
    lines.push(`  RAM: ${stats.context.totalMemoryGb} GB total / ${stats.context.freeMemoryGb ?? '?'} GB free`);
  }
  if (stats.context.platform) lines.push(`  Platform: ${stats.context.platform} ${stats.context.arch ?? ''}`);
  lines.push(
    `  Engine: ${stats.context.ollamaHost}`,
    `  Expected: ${formatDuration(low)}-${formatDuration(high)}`,
    `  Variance: ${variance}`,
  );
  return lines.join('\n');
}
